import React, { useState, useEffect } from 'react';
import { CMaskInput } from '@web-kc/shared/ui';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Search as SearchIcon, Tune as TuneIcon } from '@web-shared/core/icons';
import { useTranslation } from 'next-i18next';
import { useApp } from '@web-kc/core/providers';
import { useStyles } from './useStyles';
import LotAndTenderSearchModal from '../Modal';
import { useFilters } from '../Provider';
import { useDebouncedCallback } from 'use-debounce';
import Close from '@mui/icons-material/Close';
import { formatTimeStampShort } from '@web-kc/shared/helpers';

const DEBOUNCE_MS = 500;

const MainInput = () => {
  const { t } = useTranslation(['tenders', 'cabinet']);
  const isMp = useApp() === 'rti';
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const { classes } = useStyles({ theme, isMp });

  const {
    selectedFilters,
    setField,
    applyFilters,
    initialFilters,
    lotTypes,
    lotStatuses,
    categories,
    companies,
    regions,
    employees,
    applyDirect,
  } = useFilters();

  const debouncedApply = useDebouncedCallback(() => {
    applyFilters();
  }, DEBOUNCE_MS);

  const onKeywordChange = (value: string) => {
    setField('keyword', value);
    setField('page', 0);
    debouncedApply();
  };

  const onSearchClick = () => {
    debouncedApply.cancel();
    setField('page', 0);
    applyFilters();
  };

  const applyFromInitial = (patch: Partial<typeof initialFilters>) => {
    const next = {
      ...initialFilters,
      ...patch,
      page: 0,
    } as typeof initialFilters;
    applyDirect(next);
  };

  const removeFromArray = <
    K extends 'type' | 'status' | 'category' | 'companies',
  >(
    key: K,
    id: string | number,
  ) => {
    const arr = initialFilters[key] ?? [];
    const nextArr = arr.filter((v) => v !== id);
    applyFromInitial({ [key]: nextArr } as Partial<typeof initialFilters>);
  };

  const clearField = <K extends 'region' | 'person' | 'keyword'>(key: K) => {
    applyFromInitial({ [key]: undefined } as Partial<typeof initialFilters>);
  };

  const clearRange = (
    fromKey: keyof typeof initialFilters,
    toKey: keyof typeof initialFilters,
  ) => {
    applyFromInitial({ [fromKey]: undefined, [toKey]: undefined });
  };

  useEffect(() => {
    return () => debouncedApply.cancel();
  }, [debouncedApply]);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row', lg: 'row' }}
        mt={2.5}
        width="100%"
        gap="1px"
      >
        <CMaskInput
          type="text"
          className={classes.input}
          placeholder={t('tenders.keywords')}
          value={selectedFilters.keyword}
          onChange={(v) => onKeywordChange(v)}
          slotProps={{
            input: {
              endAdornment: selectedFilters.keyword && (
                <InputAdornment position="end">
                  <IconButton onClick={() => onKeywordChange('')} edge="end">
                    <Close />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <Stack direction={{ xs: 'column', md: 'row', lg: 'row' }}>
          <Button
            variant="contained"
            className={classes.filterButton}
            onClick={() => setOpen(true)}
          >
            <Stack direction="row" gap="10px" alignItems="center">
              <TuneIcon />
              <Typography fontSize="18px">{t('tenders.filters')}</Typography>
            </Stack>
          </Button>
          <Button
            variant="contained"
            color={isMp ? 'secondary' : 'primary'}
            className={classes.searchButton}
            onClick={onSearchClick}
          >
            <SearchIcon color="#fff" />
          </Button>
        </Stack>
      </Stack>
      <LotAndTenderSearchModal open={open} onClose={() => setOpen(false)} />

      <Stack direction="row" flexWrap="wrap" gap={1.25} mt={1.25}>
        {initialFilters.type?.map((id) => (
          <Chip
            key={`type-${id}`}
            className={classes.chips}
            label={lotTypes?.find((lt) => lt?.id === id)?.name ?? id}
            onDelete={() => removeFromArray('type', id)}
          />
        ))}

        {initialFilters.status?.map((id) => (
          <Chip
            key={`status-${id}`}
            className={classes.chips}
            label={lotStatuses?.find((ls) => ls?.id === id)?.name ?? id}
            onDelete={() => removeFromArray('status', id)}
          />
        ))}

        {initialFilters.category?.map((id) => (
          <Chip
            key={`cat-${id}`}
            className={classes.chips}
            label={categories?.find((cat) => cat?.id === id)?.name ?? id}
            onDelete={() => removeFromArray('category', id)}
          />
        ))}

        {companies &&
          initialFilters.companies?.map((id) => (
            <Chip
              key={`comp-${id}`}
              className={classes.chips}
              label={
                companies?.content?.find((com) => com?.id === id)?.fullName ??
                companies?.content?.find((com) => com?.id === id)?.name ??
                `#${id}`
              }
              onDelete={() => removeFromArray('companies', id)}
            />
          ))}

        {initialFilters.region !== undefined && (
          <Chip
            key={`reg-${initialFilters.region}`}
            className={classes.chips}
            label={
              regions?.find((reg) => reg?.id === initialFilters.region)?.name ??
              initialFilters.region
            }
            onDelete={() => clearField('region')}
          />
        )}

        {initialFilters.person !== undefined && (
          <Chip
            key={`reg-${initialFilters.person}`}
            className={classes.chips}
            label={
              employees?.content?.find(
                (emp) => emp?.personId === initialFilters.person,
              )?.name ?? initialFilters.person
            }
            onDelete={() => clearField('person')}
          />
        )}

        {initialFilters.startedAfter && initialFilters.startedBefore && (
          <Chip
            key="started-range"
            className={classes.chips}
            label={`${t('tenders.modal.startTrades')}: ${formatTimeStampShort(
              initialFilters.startedAfter,
            )} - ${formatTimeStampShort(initialFilters.startedBefore)}`}
            onDelete={() => clearRange('startedAfter', 'startedBefore')}
          />
        )}

        {initialFilters.expiredAfter && initialFilters.expiredBefore && (
          <Chip
            key="expired-range"
            className={classes.chips}
            label={`${t('tenders.modal.expiredTrades')}: ${formatTimeStampShort(
              initialFilters.expiredAfter,
            )} - ${formatTimeStampShort(initialFilters.expiredBefore)}`}
            onDelete={() => clearRange('expiredAfter', 'expiredBefore')}
          />
        )}

        {initialFilters.committedAfter && initialFilters.committedBefore && (
          <Chip
            key="committed-range"
            className={classes.chips}
            label={`${t(
              'tenders.modal.committedTrades',
            )}: ${formatTimeStampShort(
              initialFilters.committedAfter,
            )} - ${formatTimeStampShort(initialFilters.committedBefore)}`}
            onDelete={() => clearRange('committedAfter', 'committedBefore')}
          />
        )}
      </Stack>
    </Box>
  );
};

export default MainInput;
